import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { normalizeRoles } from "@/lib/normalize-user-data";
import {
  canAccessInstructorPanel,
  canAccessProfessionalJobsProfile,
  canAccessSellerPanel,
} from "@/lib/panel-switcher-access";

type PanelAccessCallableResponse = {
  success: boolean;
  roles: string[];
  subscriptionTier: string;
  subscriptionStatus: string;
  isNobility: boolean;
  isRoyalty: boolean;
  isImperial: boolean;
  canAccessSellerPanel: boolean;
  canAccessProfessionalPanel: boolean;
  canAccessInstructorPanel: boolean;
  source: string;
};

export function usePanelAccess() {
  const { user, isLoading: authLoading } = useFirebaseAuth();
  const subscription = useSubscription();

  const query = useQuery({
    queryKey: ["panel-access", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<PanelAccessCallableResponse> => {
      const callable = httpsCallable<undefined, PanelAccessCallableResponse>(
        functions,
        "getPanelAccessState"
      );
      const result = await callable();
      return result.data;
    },
  });

  return useMemo(() => {
    const localRoles = normalizeRoles(user);
    const callableData = query.data;
    const roles = callableData?.roles ?? localRoles;
    const subscriptionTier = callableData?.subscriptionTier ?? subscription.subscriptionTier ?? "FREE";
    const subscriptionStatus =
      callableData?.subscriptionStatus ?? subscription.subscriptionStatus ?? "inactive";
    const userLike = { roles };
    const subLike = {
      isNobility: callableData?.isNobility ?? subscription.isNobility,
      isRoyalty: callableData?.isRoyalty ?? subscription.isRoyalty,
      isImperial: callableData?.isImperial ?? subscription.isImperial,
      subscriptionTier,
      subscriptionStatus,
    };

    return {
      roles,
      subscriptionTier,
      subscriptionStatus,
      isNobility: subLike.isNobility,
      isRoyalty: subLike.isRoyalty,
      isImperial: subLike.isImperial,
      canAccessSellerPanel:
        callableData?.canAccessSellerPanel ?? canAccessSellerPanel(userLike, subLike),
      canAccessProfessionalPanel:
        callableData?.canAccessProfessionalPanel ??
        canAccessProfessionalJobsProfile(userLike, subLike),
      canAccessInstructorPanel:
        canAccessInstructorPanel(userLike),
      loading: authLoading || (!!user && !callableData && subscription.loading),
      source: callableData?.source ?? "local",
      error: query.error,
      refetch: query.refetch,
    };
  }, [authLoading, query.data, query.error, query.refetch, subscription, user]);
}
